/** @file Removal helper: drops removed ids from draw order, skin entries, mounts, weights, grip-2 bones. */
import type { Doc } from '../doc/schema';
import type { EntityKind } from '../doc/entityKinds';
import type { AttachmentDoc } from '../doc/schemaParts/attachments';
import { dropKeys } from './dropKeys';
import { withoutRemoved } from './withoutRemoved';

type Removed = ReadonlyMap<string, EntityKind>;
type Influence = [string, number];

function renormalized(vertex: Influence[], removed: Removed): Influence[] {
  const left = vertex.filter(([bone]) => !removed.has(bone));
  if (left.length === vertex.length) return vertex;
  const sum = left.reduce((s, [, w]) => s + w, 0);
  return left.map(([bone, w]): Influence => [bone, sum > 0 ? w / sum : 0]);
}

function stripWeights(att: AttachmentDoc, removed: Removed): void {
  if (att.type !== 'mesh') return;
  const g = att.geometry;
  if (g.kind !== 'skinned') return;
  dropKeys(g.bindPose, (bone) => removed.has(bone));
  if (g.weights.some((v) => v.some(([bone]) => removed.has(bone)))) g.weights = g.weights.map((v) => renormalized(v, removed));
}

/** Unsets a surviving socket's optional grip-2 bone when that bone is removed (the socket and its item stay). */
function stripGrip2(draft: Doc, removed: Removed): void {
  for (const socket of Object.values(draft.sockets)) if (socket.grip2Bone !== undefined && removed.has(socket.grip2Bone)) delete socket.grip2Bone;
}

/** Drops removed ids from draw order, skin entries, mounts, weights and socket grip-2 bones. */
export function stripSkeletonRefs(draft: Doc, removed: Removed): void {
  const order = withoutRemoved(draft.drawOrder, (s) => removed.has(s));
  if (order) draft.drawOrder = order;
  for (const skin of Object.values(draft.skins)) {
    const entries = withoutRemoved(skin.entries, (e) => removed.has(e.slot) || removed.has(e.attachment));
    if (entries) skin.entries = entries;
  }
  for (const att of Object.values(draft.attachments)) {
    if (att.mount && removed.has(att.mount.socket)) delete att.mount;
    stripWeights(att, removed);
  }
  stripGrip2(draft, removed);
}
